import { api } from '../api.js';
import { state } from '../state.js';
import { setDue, project as store } from '../project-store.js';
import { cards } from '../cards-store.js';
import { RouterLink } from '../router.js';
import { Empty, humanDate } from '../ui.js';
import { VoiceInput, heardHeader } from '../speech.js';
import { Talk } from '../talk.js';
import { ensureDictionaries } from '../bootstrap.js';

// Повторы: три упражнения на одном экране, все под телефон в дороге.
//
//   Повторы   — вопросы, на которых ты плыл, с возвратом по забыванию.
//   Не знаю   — вопросы по темам, которых у тебя ни разу не спрашивали.
//               Оценивается не знание, а выход из положения: честное «не
//               сталкивался, разбирался бы так» против блефа.
//   Мои ответы — то, что уже засчитано как уверенное. Читаешь перед звонком.
//
// Ответ сохраняется, даже если проверить его некем: агент живёт на машине
// человека, а она может быть выключена.

export const Cards = {
  components: { RouterLink, Empty, Talk },
  data() {
    // Здесь только то, что живёт ровно на этом экране и ровно сейчас: запись
    // с микрофона, палец на карточке, «отправляю». Данные — в cards-store.js.
    return {
      touch: null,
      listening: false,
      level: [],
      showHeard: false,
      all: [],            // вся колода: и созревшие, и замолчавшие
      editing: 0,
      editText: '',
      rephrasing: false,
      rephrased: 0,
      history: null,      // прошлые попытки по текущей карточке
      clip: 0,            // голос текущего ответа: цепляется при отправке
      playing: 0,
      player: null,
      historyFor: 0,
      speaking: false,
      speaker: null,
      capturing: null,
      status: '',
      sending: false,
      attempt: 1,
      voiceIn: null,
    };
  },
  computed: {
    /** Счётчик повторов общий с шапкой проекта: у каждого свой — они разъезжаются. */
    due() { return store.due; },

    // Данные экрана повторов лежат в сторе и переживают уход на другую
    // вкладку. Здесь короткие переходники, чтобы разметка осталась прежней.
    mode: { get() { return cards.mode; }, set(v) { cards.mode = v; } },
    deck: { get() { return cards.deck; }, set(v) { cards.deck = v; } },
    idx: { get() { return cards.idx; }, set(v) { cards.idx = v; } },
    probe: { get() { return cards.probe; }, set(v) { cards.probe = v; } },
    library: { get() { return cards.library; }, set(v) { cards.library = v; } },
    drafts: { get() { return cards.drafts; }, set(v) { cards.drafts = v; } },
    results: { get() { return cards.results; }, set(v) { cards.results = v; } },
    stats: { get() { return cards.stats; }, set(v) { cards.stats = v; } },
    probeStats: { get() { return cards.probeStats; }, set(v) { cards.probeStats = v; } },
    needAgent: { get() { return cards.needAgent; }, set(v) { cards.needAgent = v; } },
    skipped: { get() { return cards.skipped; }, set(v) { cards.skipped = v; } },
    deckLoading: { get() { return cards.loading; }, set(v) { cards.loading = v; } },
    deckError: { get() { return cards.error; }, set(v) { cards.error = v; } },
    project() { return state.projects.find((p) => p.id === state.projectId) || null; },
    hasProject() { return Boolean(state.projectId); },
    current() { return this.mode === 'unknown' ? this.probe : (this.deck[this.idx] || null); },
    card() { return this.deck[this.idx] || null; },

    /**
     * Черновик и вердикт живут при карточке, а не при экране: иначе шаг назад
     * стирал набранный текст и уже полученный разбор.
     */
    draft: {
      get() { return this.drafts[this.key] || ''; },
      set(v) { this.drafts = { ...this.drafts, [this.key]: v }; },
    },
    result() { return this.results[this.key] || null; },
    key() { return this.mode === 'unknown' ? `p${this.probe?.id}` : `c${this.card?.id}`; },

    hasPrev() { return this.mode !== 'unknown' && this.idx > 0; },
    hasNext() { return this.mode !== 'unknown' && this.deck.length > 1; },

    /** Сдвиг ленты: активная карточка по центру, соседние выглядывают с краёв. */
    trackShift() {
      return { transform: `translateX(calc((100% - var(--slide)) / 2 - `
        + `${this.idx} * (var(--slide) + var(--slide-gap))))` };
    },
    verdictWord() {
      const words = this.mode === 'unknown'
        ? { strong: 'Хорошо вышел', ok: 'Признал, но растерялся', weak: 'Так не надо' }
        : { strong: 'Засчитано', ok: 'Неполно', weak: 'Мимо' };
      return words[this.result?.verdict] || '';
    },
  },
  async created() {
    await ensureDictionaries();
    // Первым параметром идёт уровень звука, а не текст: черновика от браузера
    // больше нет, распознаёт сервер по «Стоп». Пока это не поправили, полосы
    // уровня приезжали прямо в поле ответа строкой «1,0.53,0.19,0,0».
    this.voiceIn = new VoiceInput(
      (bands) => { this.level = bands; },
      (m) => { this.status = m; });
    await this.load();
  },
  beforeUnmount() { this.voiceIn?.stop(); this.hush(); },
  methods: {
    async switchMode(mode) {
      this.mode = mode;
      this.status = '';
      this.attempt = 1;
      this.idx = 0;
      await this.load();
    },

    async load() {
      if (!state.projectId) { this.deckLoading = false; return; }
      this.deckLoading = true;
      this.deckError = '';
      this.needAgent = false;
      try {
        if (this.mode === 'all') {
          const { rows } = await api.get(`/api/cards/${state.projectId}/all`);
          this.all = rows;
        } else if (this.mode === 'library') {
          const { rows } = await api.get(`/api/cards/${state.projectId}/library`);
          this.library = rows;
        } else if (this.mode === 'unknown') {
          const data = await api.get(`/api/cards/${state.projectId}/probe`);
          this.probe = data.probe;
          this.probeStats = data.stats;
        } else {
          const data = await api.get(
            `/api/cards/${state.projectId}/next?skip=${this.skipped.join(',')}`);
          this.deck = data.cards || (data.card ? [data.card] : []);
          this.idx = Math.min(this.idx, Math.max(0, this.deck.length - 1));
          setDue(data.due);
          this.stats = data.stats;
        }
      } catch (e) {
        // Вопрос по слепой зоне сочиняется моделью. Нет агента — сказать об
        // этом спокойно: краснеть тут нечему, две соседние вкладки читают базу
        // и работают всегда.
        if (this.mode === 'unknown' && e.status === 503) this.needAgent = true;
        else this.deckError = e.message;
      } finally {
        this.deckLoading = false;
      }
    },

    async mic() {
      if (this.listening) return this.stopMic();
      // Озвучка глохнет, как только берут микрофон: он пишет сырой сигнал, без
      // подавления эха, и эталонный ответ из колонок лёг бы прямо в запись.
      this.hush();
      this.listening = true;
      await this.voiceIn.start();
      this.status = 'слушаю — договаривай и жми «Стоп»';
    },

    stopMic() {
      if (!this.capturing) {
        this.capturing = this.doStop().finally(() => { this.capturing = null; });
      }
      return this.capturing;
    },

    async doStop() {
      this.listening = false;
      this.level = [];
      const said = await this.voiceIn.stop();
      if (!said) { this.status = ''; return; }
      this.status = 'распознаю…';
      try {
        // Распознавание живёт на сервере и раннера не требует — работает даже
        // при выключенном ноутбуке. heard — голос третьего движка, браузерного.
        const { text, clip } = await api.raw('/api/media/recognize', said.blob,
          heardHeader(said.heard));
        // Клип цепляется к ответу при отправке: записан он раньше, чем ответ
        // появился, и до «Отправить» цеплять его не к чему.
        if (clip) this.clip = clip;
        if (text) {
          // Дописываем к набранному: человек мог начать текстом и договорить
          // голосом. Затирать написанное нельзя.
          const had = this.draft.trim();
          this.draft = had ? `${had} ${text}` : text;
          this.status = 'распознал — проверь и отправляй';
        }
        else this.status = 'ничего не расслышал';
      } catch (e) {
        this.status = 'распознать не вышло: ' + e.message;
      }
    },

    humanDate,

    /**
     * Жизненный цикл карточки одной строкой.
     *
     * Раньше было непонятно, откуда карточка взялась и когда исчезнет: человек
     * видел вопрос и всё. А правило простое — верно подряд, и срок растёт:
     * день, три, неделя, три недели; провал сбрасывает в начало.
     */
    life(card) {
      const good = Math.max(0, (card.tries || 0) - (card.fails || 0));
      if (!card.tries) return 'новая — раньше на неё отвечал плохо';
      const back = card.вернётся_через || 1;
      const word = back === 1 ? 'день' : back < 5 ? 'дня' : 'дней';
      const streak = card.last_verdict === 'weak'
        ? 'прошлый раз провалил'
        : `подряд верно: ${good}`;
      return `повторов: ${card.tries} · ${streak} · ответишь верно — вернётся через ${back} ${word}`;
    },

    /** Свой ответ вслух: по звуку слышно то, чего не видно в расшифровке. */
    hear(row) {
      if (this.playing === row.clip_id) return this.hushCard();
      this.hushCard();
      this.playing = row.clip_id;
      this.player = new Audio(`/api/media/clips/${row.clip_id}`);
      this.player.onended = () => this.hushCard();
      this.player.play().catch(() => this.hushCard());
    },

    hushCard() {
      if (this.player) { this.player.pause(); this.player.src = ''; }
      this.player = null;
      this.playing = 0;
    },

    /** Слово вердикта для списка прошлых попыток: те же, что и в свежем. */
    verdictName(verdict) {
      return { strong: 'Засчитано', ok: 'Неполно', weak: 'Мимо' }[verdict] || 'без вердикта';
    },

    /**
     * Прошлые попытки по этой же карточке.
     *
     * Без них жизненный цикл карточки не виден: ответил, получил вердикт — и
     * всё исчезло. А смысл повтора в том, чтобы сравнить сегодняшний ответ со
     * вчерашним.
     */
    /**
     * Переписать формулировки по текущему правилу.
     *
     * Правило менялось — в колоде остались карточки, написанные по старому, и
     * сами они не исправятся. Идёт пачками по тридцать, поэтому кнопку жмут,
     * пока счётчик не станет нулём.
     */
    async rephraseAll() {
      this.rephrasing = true;
      this.deckError = '';
      try {
        const { done } = await api.post(`/api/cards/${state.projectId}/rephrase`, {});
        this.rephrased = done;
        await this.load();
      } catch (e) { this.deckError = e.message; }
      finally { this.rephrasing = false; }
    },

    /** Переписать формулировку руками: агент ошибся, а гонять всю колоду дорого. */
    startEdit(card) {
      this.editing = card.id;
      this.editText = card.question;
    },

    async saveEdit(card) {
      const card_ = this.editText.trim();
      if (card_.length < 5) return;
      try {
        await api.post(`/api/cards/${state.projectId}/edit`,
          { question: card.id, card: card_ });
        card.question = card_;
        this.editing = 0;
      } catch (e) { this.deckError = e.message; }
    },

    /**
     * Выбросить карточку из колоды.
     *
     * Сам вопрос остаётся в реестре заданного — он часть истории собеса, и
     * интервьюер по нему знает, что уже спрашивал. Уходит только повторение.
     */
    async dropCard(card) {
      try {
        const { due } = await api.post(`/api/cards/${state.projectId}/drop`,
          { question: card.id });
        this.all = this.all.filter((c) => c.id !== card.id);
        if (due !== undefined) setDue(due);
      } catch (e) { this.deckError = e.message; }
    },

    /** Когда карточка вернётся: главное, чего на экране не было. */
    dueLabel(card) {
      if (!card.tries) return 'ещё не отвечал';
      if (card.last_verdict === 'weak') return 'провалил — вернётся сразу';
      if (!card.вернётся) return 'готова к повтору';
      const days = Math.ceil((new Date(card.вернётся) - Date.now()) / 86400000);
      if (days <= 0) return 'готова к повтору';
      return `молчит ещё ${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'}`;
    },

    /** История по конкретной карточке — открывается и до ответа, и после. */
    showHistory(card) {
      return this.openHistory(card);
    },

    async openHistory(only = null) {
      const card = only || this.current;
      if (!card) return;
      if (this.historyFor === card.id) { this.historyFor = 0; this.history = null; return; }
      this.historyFor = card.id;
      this.history = null;
      const data = await api.get(`/api/cards/${state.projectId}/history/${card.id}`)
        .catch(() => null);
      if (data && this.historyFor === card.id) this.history = data.rows;
    },

    /** Озвучить правильный ответ: слушать полезнее, чем перечитывать. */
    sayCorrect(text) {
      if (this.speaking) return this.hush();
      const q = new URLSearchParams({ text: String(text).slice(0, 2000) });
      const audio = new Audio(`/api/media/tts?${q}`);
      audio.onended = () => this.hush();
      audio.onerror = () => this.hush();
      audio.play().catch(() => this.hush());
      this.speaker = audio;
      this.speaking = true;
    },

    hush() {
      if (this.speaker) { this.speaker.pause(); this.speaker.src = ''; }
      this.speaker = null;
      this.speaking = false;
    },

    async send() {
      this.showHeard = false;
      if (this.listening || this.capturing) await this.stopMic();
      const text = this.draft.trim();
      if (!text) { this.status = 'пусто — нечего отправлять'; return; }

      this.sending = true;
      this.status = 'проверяю…';
      const key = this.key;
      try {
        const result = this.mode === 'unknown'
          ? await api.post(`/api/cards/${state.projectId}/probe/${this.probe.id}`, { text })
          : await api.post(`/api/cards/${state.projectId}/answer`,
            { question: this.card.id, text, ...(this.clip ? { clip: this.clip } : {}) });
        this.clip = 0;
        this.results = { ...this.results, [key]: result };
        // Счётчик повторов приходит вместе с проверкой: и шапка проекта, и
        // заголовок вкладки читают одно и то же число.
        if (result.due !== undefined) setDue(result.due);
        this.status = '';
      } catch (e) {
        this.status = 'не отправилось: ' + e.message;
      } finally {
        this.sending = false;
      }
    },

    /**
     * Тот же вопрос ещё раз, сразу. Карточки разносят повторы по дням, а
     * иногда нужно наоборот: проговорить одно и то же подряд, пока не
     * зазвучит гладко. Перед конкретным собесом это работает лучше.
     */
    again() {
      const { [this.key]: _gone, ...rest } = this.results;
      this.results = rest;
      this.draft = '';
      this.status = '';
      this.attempt += 1;
    },

    /** Шаг по колоде. Кончилась — идём за новой пачкой. */
    async go(delta) {
      const to = this.idx + delta;
      this.status = '';
      this.attempt = 1;
      if (to < 0) return;
      if (to >= this.deck.length) {
        this.idx = 0;
        return this.load();
      }
      this.idx = to;
    },

    async nextCard() {
      if (this.mode === 'unknown') { this.results = {}; this.drafts = {}; return this.load(); }
      await this.go(1);
    },

    async skip() {
      // Пропущенное не возвращаем в этом заходе: иначе следующая пачка
      // начнётся с того же вопроса.
      if (this.card) this.skipped.push(this.card.id);
      const at = this.idx;
      this.deck = this.deck.filter((_, i) => i !== at);
      if (!this.deck.length) { this.idx = 0; return this.load(); }
      this.idx = Math.min(at, this.deck.length - 1);
      this.status = '';
      this.attempt = 1;
    },

    // Свайп: экран задуман для телефона в дороге, и листать там пальцем
    // естественнее, чем целиться в стрелку.
    onTouchStart(e) { this.touch = e.changedTouches[0].clientX; },
    onTouchEnd(e) {
      if (this.touch === null) return;
      const dx = e.changedTouches[0].clientX - this.touch;
      this.touch = null;
      if (Math.abs(dx) < 60) return;
      this.go(dx < 0 ? 1 : -1);
    },

    async catchUp() {
      this.status = 'разбираю накопившееся…';
      try {
        const { judged } = await api.post(`/api/cards/${state.projectId}/catch-up`);
        this.status = judged.length ? `проверено: ${judged.length}` : 'раннер всё ещё не на связи';
        await this.load();
      } catch (e) {
        this.status = 'не вышло: ' + e.message;
      }
    },
  },
  template: `
    <div class="cards">

      <!-- Сегментами, а не вкладками: вкладки проекта уже стоят выше, и вторая
           такая же полоса читалась как их копия. -->
      <div class="row" style="align-items:center;gap:12px;margin-bottom:14px">
        <div class="seg">
          <button :class="{ on: mode === 'repeat' }" @click="switchMode('repeat')">
            Повторы<i v-if="due">{{ due }}</i>
          </button>
          <button :class="{ on: mode === 'all' }" @click="switchMode('all')">Вся колода</button>
          <button :class="{ on: mode === 'unknown' }" @click="switchMode('unknown')">Не знаю</button>
          <button :class="{ on: mode === 'library' }" @click="switchMode('library')">Мои ответы</button>
        </div>
      </div>

      <p class="sub cards-how" v-if="mode === 'repeat' && hasProject">
        Сюда попадают вопросы, на которые ты ответил слабо или неполно — их
        отбирает разбор собеса. Ответил верно — карточка замолкает
        <strong>на день</strong>, потом на три, на неделю, на три недели.
        Ошибся — возвращается завтра и счёт начинается заново. Совсем не
        исчезает: раз в три недели всплывёт даже то, что знаешь твёрдо.
      </p>

      <p class="err" v-if="deckError">{{ deckError }}</p>

      <Empty v-if="!hasProject" title="Проект не выбран"
             hint="Открой проект на странице «Проекты» — упражнения берутся из его собесов.">
      </Empty>

      <!-- библиотека -->
      <template v-else-if="mode === 'library'">
        <p class="sub" style="margin-top:0">
          Ответы, засчитанные как уверенные. Это то, что ты уже умеешь
          рассказывать — читай перед звонком.
        </p>
        <Empty v-if="!deckLoading && !library.length" title="Пока пусто"
               hint="Сюда попадают ответы из повторов, засчитанные на «уверенно».">
        </Empty>
        <div class="card qa" v-for="r in library" :key="r.question_id">
          <span class="tag">{{ r.topic }}</span>
          <p class="ask">{{ r.question }}</p>
          <div class="reference">{{ r.answer }}</div>
          <p class="faint" v-if="r.note" style="margin:8px 0 0">{{ r.note }}</p>
        </div>
      </template>

      <!-- тренировка «не знаю» -->
      <template v-else-if="mode === 'all'">
        <p class="sub cards-how">
          Здесь колода целиком — вместе с теми, что уже замолчали до срока.
          Во вкладке «Повторы» их не видно нарочно: там только то, что пора
          спросить сегодня.
        </p>
        <div class="row" style="margin-bottom:12px">
          <button class="ghost sm" :disabled="rephrasing" @click="rephraseAll">
            {{ rephrasing ? 'переписываю…' : 'Переформулировать карточки' }}
          </button>
          <span class="faint" v-if="rephrased">переписано: {{ rephrased }}</span>
        </div>
        <Empty v-if="!deckLoading && !all.length" title="Колода пуста"
               hint="Карточки появляются из разборов собесов: туда идут вопросы,
                     на которые ответил слабо или неполно.">
        </Empty>
        <div class="card deck-row" v-for="c in all" :key="c.id">
          <div class="row">
            <span class="tag">{{ c.topic }}</span>
            <span class="spacer"></span>
            <span class="faint">{{ dueLabel(c) }}</span>
          </div>
          <p class="ask">{{ c.question }}</p>
          <div class="row card-foot">
            <span class="faint">
              повторов: {{ c.tries }}<template v-if="c.origin_seq">
              · из собеса №{{ c.origin_seq }}</template>
            </span>
            <span class="spacer"></span>
            <button class="link" @click="startEdit(c)">переписать</button>
            <button class="link" @click="dropCard(c)">выбросить</button>
            <button class="link" @click="showHistory(c)">
              {{ historyFor === c.id ? 'скрыть историю' : 'история ответов' }}
            </button>
          </div>
          <div class="row" v-if="editing === c.id" style="margin-top:8px">
            <textarea v-model="editText" rows="3" style="flex:1"></textarea>
            <button class="ghost sm" @click="saveEdit(c)">Сохранить</button>
            <button class="link" @click="editing = 0">отмена</button>
          </div>
          <div class="past inline" v-if="historyFor === c.id">
            <p class="faint" v-if="!history">смотрю…</p>
            <p class="faint" v-else-if="!history.length">на эту карточку ещё не отвечал</p>
            <div class="try" v-for="h in history || []" :key="h.id">
              <div class="row">
                <span class="tag" :class="h.verdict">{{ verdictName(h.verdict) }}</span>
                <span class="faint">{{ humanDate(h.created_at) }}</span>
                <button class="link" v-if="h.clip_id" @click="hear(h)">
                  {{ playing === h.clip_id ? 'стоп' : 'мой голос' }}
                </button>
              </div>
              <p class="said">{{ h.answer }}</p>
              <details class="right" v-if="h.correct">
                <summary>как было правильно</summary>
                <p>{{ h.correct }}</p>
              </details>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="mode === 'unknown'">
        <p class="sub" style="margin-top:0">
          Вопрос по теме, которой у тебя ещё не спрашивали. Отвечать по существу
          скорее всего не выйдет — это и есть замысел. Считается не знание, а
          как ты вышел: честное «не сталкивался, разбирался бы так» против блефа.
        </p>
        <Empty v-if="!deckLoading && needAgent" title="Нужен запущенный агент"
               hint="Этот вопрос сочиняется на месте, поэтому без агента его неоткуда взять.
                     Повторы и «Мои ответы» работают всегда: там уже готовые вопросы.">
          <router-link class="btn" to="/settings/agent">Открыть настройки агента</router-link>
        </Empty>
        <Empty v-else-if="!deckLoading && !probe" title="Слепых зон не осталось"
               hint="Все темы направления уже всплывали на твоих собесах. Это редкость.">
        </Empty>
      </template>

      <!-- повторы -->
      <template v-else>
        <p class="sub" style="margin-top:0">
          Вопросы, на которых ты плыл на собесах. Полминуты на ответ — и дальше.
        </p>
        <Empty v-if="!deckLoading && !deck.length" title="На сегодня всё"
               hint="Просроченных карточек нет. Они вернутся сами: провалил — завтра, закрыл подряд — через три дня, потом через неделю.">
        </Empty>
      </template>

      <!-- вопрос и ответ: общая часть повторов и «не знаю» -->
      <template v-if="hasProject && mode !== 'library' && current">
        <div class="deck" @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
          <div class="track" :style="trackShift">
            <div class="slide" v-for="(c, i) in (mode === 'unknown' ? [current] : deck)" :key="c.id"
                 :class="{ on: mode === 'unknown' || i === idx }"
                 @click="mode !== 'unknown' && i !== idx && go(i - idx)">
              <div class="card question">
                <div class="row">
                  <span class="tag">{{ c.topic }}</span>
                  <span class="spacer"></span>
                  <span class="faint" v-if="attempt > 1 && i === idx">попытка {{ attempt }}</span>
                  <span class="tick" v-else-if="results['c' + c.id]"
                        :class="results['c' + c.id].verdict || 'pending'">●</span>
                </div>
                <p class="ask">{{ c.question }}</p>
                <p class="faint life">{{ life(c) }}</p>
                <div class="row card-foot" v-if="i === idx">
                  <span class="faint" v-if="c.origin_seq">
                    из собеса №{{ c.origin_seq }}
                  </span>
                  <span class="spacer"></span>
                  <button class="link" @click.stop="showHistory(c)">
                    {{ historyFor === c.id ? 'скрыть историю' : 'история ответов' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row deck-nav" v-if="mode !== 'unknown'">
          <button class="ghost sm" @click="go(-1)" :disabled="!hasPrev" title="предыдущая">←</button>
          <!-- «3 из 12» рядом со счётчиком «Повторы 35» читалось как ошибка:
               двенадцать — это размер пачки, а тридцать пять — вся очередь.
               Показываем одну величину, ту же, что в заголовке вкладки. -->
          <span class="faint" :title="'в пачке ' + deck.length + ', подгружается по мере ответов'">
            осталось {{ due || deck.length }}
          </span>
          <button class="ghost sm" @click="go(1)" :disabled="!hasNext" title="следующая">→</button>
          <span class="spacer"></span>
          <span class="faint">листается свайпом</span>
        </div>

        <template v-if="!result">
          <!-- Пока идёт запись, вместо поля — уровень звука: тот же приём, что
               на собесе. Он отвечает на единственный вопрос, который есть у
               говорящего, — слышно меня или нет. -->
          <div class="hear" v-if="listening">
            <span class="wave" aria-hidden="true">
              <b v-for="(v, i) in (level.length ? level : [0,0,0,0,0])" :key="i"
                 :style="{ transform: 'scaleY(' + (0.12 + v * 0.88) + ')' }"></b>
            </span>
            <span>слушаю — текст появится, когда нажмёшь «Стоп»</span>
          </div>
          <textarea v-model="draft" rows="4" class="answer"
                    :placeholder="listening ? 'можно и напечатать — запись идёт' : 'ответь голосом или напиши'"></textarea>
          <div class="row cards-actions">
            <button class="mic" :class="{ recording: listening }" @click="mic" :disabled="sending">
              <template v-if="listening"><span class="ico" aria-hidden="true"><svg viewBox="0 0 16 16"><rect x="3" y="3" width="10" height="10" rx="2"/></svg></span> Стоп</template>
              <template v-else><span class="ico" aria-hidden="true"><svg viewBox="0 0 16 16"><rect x="6" y="2" width="4" height="7" rx="2"/><path d="M4 8a4 4 0 0 0 8 0M8 12v2"/></svg></span> Ответить</template>
            </button>
            <button class="primary" @click="send" :disabled="sending">Проверить</button>
            <button class="ghost" @click="skip" :disabled="sending">Пропустить</button>
          </div>
          <div class="status" :class="{ busy: sending, live: listening }" v-if="status">
            <i class="dots" v-if="sending || listening"><b></b><b></b><b></b></i>
            <span>{{ status }}</span>
          </div>
        </template>

        <template v-else>
          <div class="card verdict-card" :class="result.verdict || 'pending'">
            <strong v-if="result.pending">Ответ сохранён</strong>
            <strong v-else>{{ verdictWord }}</strong>
            <p v-if="result.pending" style="margin:6px 0 0">
              Проверить было некем: агент на твоей машине не запущен. Разбор
              появится, когда раннер вернётся.
            </p>
            <p v-else style="margin:6px 0 0">{{ result.note }}</p>

            <!-- Как проверяющий понял распознанную речь. На собесе это уже
                 было, в повторах — нет, хотя ошибается распознавание здесь
                 ровно так же. Открывается по клику: висеть всегда ему незачем. -->
            <p class="heard" v-if="result.heard && showHeard">понял так: {{ result.heard }}</p>
            <a href="#" class="faint sm" v-if="result.heard && !showHeard"
               @click.prevent="showHeard = true">как понял мой ответ</a>
          </div>

          <!-- Как правильно — отдельной карточкой, а не строкой в вердикте:
               ради этого повтор и существует. На вопрос со словом «почему»
               здесь обязан быть механизм, а не только итог. -->
          <div class="card correct" v-if="result.answer">
            <strong>Как правильно</strong>
            <p style="margin:6px 0 0">{{ result.answer }}</p>
            <button class="ghost sm" @click="sayCorrect(result.answer)" style="margin-top:10px">
              <span class="ico" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M4 6h2l3-2.5v9L6 10H4z"/><path d="M11 5.5a3.5 3.5 0 0 1 0 5"/></svg></span>
              {{ speaking ? 'стоп' : 'прослушать' }}
            </button>
          </div>

          <div class="row cards-actions">
            <button class="primary" @click="nextCard">Дальше</button>
            <button class="ghost" @click="again">Ещё раз этот же</button>
            <button class="ghost" @click="openHistory">
              {{ historyFor === current.id ? 'Скрыть прошлые' : 'Прошлые ответы' }}
            </button>
            <button class="ghost" v-if="result.pending" @click="catchUp">Проверить накопившееся</button>
          </div>

          <div class="card past" v-if="historyFor === current.id">
            <strong>Как отвечал раньше</strong>
            <p class="faint" v-if="history && !history.length" style="margin:6px 0 0">
              Это первая проверенная попытка по этой карточке.
            </p>
            <p class="faint" v-if="!history" style="margin:6px 0 0">смотрю…</p>
            <div class="try" v-for="h in history || []" :key="h.id">
              <div class="row">
                <span class="tag" :class="h.verdict">{{ verdictName(h.verdict) }}</span>
                <span class="faint">{{ humanDate(h.created_at) }}</span>
                <button class="link" v-if="h.clip_id" @click="hear(h)">
                  {{ playing === h.clip_id ? 'стоп' : 'мой голос' }}
                </button>
              </div>
              <p class="said">{{ h.answer }}</p>
              <p class="faint" v-if="h.note">{{ h.note }}</p>
              <details class="right" v-if="h.correct">
                <summary>как было правильно</summary>
                <p>{{ h.correct }}</p>
              </details>
              <p class="faint" v-if="!h.verdict">проверка отложена — раннер был выключен</p>
            </div>
          </div>

          <Talk :question="Number(current.id)" v-if="mode !== 'unknown' && current" />
        </template>
      </template>

      <p class="faint" v-if="mode === 'repeat' && stats && Number(stats.проверено)" style="margin-top:24px">
        Всего повторов: {{ stats.проверено }}, из них уверенно: {{ stats.уверенно }}.
      </p>
      <p class="faint" v-if="mode === 'unknown' && probeStats && Number(probeStats.попыток)" style="margin-top:24px">
        Попыток: {{ probeStats.попыток }}, вышел достойно: {{ probeStats.удачно }}.
      </p>
    </div>`,
};
